import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import SearchableTable from "../../../components/Tables/SearchableTable";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import AddCashAdjustmentModal from "../../../components/Modals/AddCashAdjustmentModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccounts } from "../../../store/Slices/AccountSlice";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import { fetchSuppliers } from "../../../store/Slices/SupplierSlice";
import { fetchCashBooks } from "../../../store/Slices/CashBookSlice";
import {
  deleteCashAdjustment,
  fetchCashAdjustments,
} from "../../../store/Slices/CashAdjustmentSlice";
import { GetCompanyPaymentsApi, GetCustomerPaymentsApi } from "../../../ApiRequests";
import MetricCard from "../../../components/Cards/MetricCard";
import { isSuperAdminRole } from "../../../utils/appPaths";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";

const num = (v) => Number(v || 0);

const tabs = [
  { label: "All Accounts" },
  { label: "All Payments" },
  { label: "Cash Adjustment" },
  { label: "Current Value" },
  { label: "All Transactions" },
];

const Finance = () => {
  const dispatch = useDispatch();
  const AccountState = useSelector((state) => state.AccountState);
  const Customers = useSelector((state) => state.Customers);
  const Suppliers = useSelector((state) => state.Suppliers);
  const CashBookState = useSelector((state) => state.CashBookState);
  const CashAdjustmentState = useSelector((state) => state.CashAdjustmentState);

  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [openAdjustmentModal, setOpenAdjustmentModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const isSuperAdmin = isSuperAdminRole();

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const storedBranch = localStorage.getItem("branch");
  const branchId =
    userData.role === 2
      ? userData.branchId
      : userData.branchId || storedBranch || "";

  useEffect(() => {
    dispatch(fetchAccounts());
    dispatch(fetchCashAdjustments());
    dispatch(fetchCashBooks({ branchId }));
  }, [dispatch, branchId]);

  useEffect(() => {
    if (activeTab === 1) {
      const load = async () => {
        try {
          setPaymentLoading(true);
          const [customerResp, companyResp] = await Promise.all([
            GetCustomerPaymentsApi(),
            GetCompanyPaymentsApi(),
          ]);
          const customerRows = (customerResp?.data?.data?.payload || []).map((row) => ({
            ...row,
            user_name: row.customer_name || "-",
            type_label: "Customer",
          }));
          const companyRows = (companyResp?.data?.data?.payload || []).map((row) => ({
            ...row,
            user_name: row.company_name || "-",
            type_label: "Company",
          }));
          setPaymentRows([...customerRows, ...companyRows]);
        } catch (error) {
          setPaymentRows([]);
        } finally {
          setPaymentLoading(false);
        }
      };
      load();
    }
    if (activeTab === 3) {
      dispatch(fetchCustomers());
      dispatch(fetchSuppliers());
    }
    if (activeTab === 4) {
      dispatch(fetchCashBooks({ branchId }));
    }
  }, [activeTab, dispatch, branchId]);

  const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];
  const adjustments = Array.isArray(CashAdjustmentState.data)
    ? CashAdjustmentState.data
    : [];
  const transactions = Array.isArray(CashBookState.data?.entries)
    ? CashBookState.data.entries
    : [];

  const type1Total = useMemo(
    () =>
      accounts
        .filter((acc) => Number(acc.type) === 1 || String(acc.account_name).toUpperCase() === "CASH")
        .reduce((sum, acc) => sum + num(acc.amount), 0),
    [accounts]
  );
  const type2Total = useMemo(
    () =>
      accounts
        .filter((acc) => String(acc.account_name || "").toUpperCase() !== "CASH")
        .reduce((sum, acc) => sum + num(acc.amount), 0),
    [accounts]
  );

  const currentValue = useMemo(() => {
    const customers = Array.isArray(Customers.data) ? Customers.data : [];
    const suppliers = Array.isArray(Suppliers.data) ? Suppliers.data : [];
    const receivable = customers.reduce((sum, c) => sum + num(c.recieveable), 0);
    const payable = suppliers.reduce((sum, c) => sum + num(c.payable), 0);
    const cash = accounts
      .filter((acc) => String(acc.account_name || "").toUpperCase() === "CASH")
      .reduce((sum, acc) => sum + num(acc.amount), 0);
    const bank = accounts
      .filter((acc) => String(acc.account_name || "").toUpperCase() !== "CASH")
      .reduce((sum, acc) => sum + num(acc.amount), 0);
    return { receivable, payable, cash, bank, total: cash + bank + receivable - payable };
  }, [Customers.data, Suppliers.data, accounts]);

  const refreshAdjustments = () => {
    dispatch(fetchCashAdjustments());
    dispatch(fetchAccounts());
    dispatch(fetchCashBooks({ branchId }));
  };

  return (
    <div className="w-full flex flex-col px-4 pt-2 pb-8 gap-y-4">
      <Header title="Finance" desc="Accounts, payments, adjustments and cash flow">
        {activeTab === 2 && (
          <CustomBtn title="Add Cash Adjustment" onClick={() => setOpenAdjustmentModal(true)} />
        )}
      </Header>

      <div className="z-10 mb-3">
        <CustomTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      {activeTab === 0 && (
        <SearchableTable
          SearchPlaceholder="Search account..."
          SearchText={searchText}
          setSearchText={setSearchText}
          enableAccountLog
          CurrentData={accounts}
          Columns={[
            { id: "actions", title: "Actions" },
            { id: "account_name", title: "Account" },
            { id: "account_no", title: "Account No" },
            { id: "opening_balance", title: "Opening Balance" },
            { id: "amount", title: "Current Amount" },
          ]}
        />
      )}

      {activeTab === 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Cash Total"
            value={type1Total.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Current available cash balances"
            tone="success"
          />
          <MetricCard
            title="Bank Total"
            value={type2Total.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Current available bank balances"
            tone="info"
          />
        </div>
      )}

      {activeTab === 1 &&
        (paymentLoading ? (
          <FetchingLoading />
        ) : (
          <SearchableTable
            SearchPlaceholder="Search payments..."
            SearchText={searchText}
            setSearchText={setSearchText}
            CurrentData={paymentRows}
            Columns={[
              { id: "date", title: "Date" },
              { id: "type_label", title: "Type" },
              { id: "user_name", title: "Party" },
              { id: "bank_name", title: "Bank" },
              { id: "desc", title: "Description" },
              { id: "amount", title: "Amount" },
            ]}
          />
        ))}

      {activeTab === 2 &&
        (CashAdjustmentState.loading ? (
          <FetchingLoading />
        ) : (
          <SearchableTable
            SearchPlaceholder="Search adjustments..."
            SearchText={searchText}
            setSearchText={setSearchText}
            CurrentData={adjustments.map((row) => ({
              ...row,
              from_account: row.fromAccountId?.account_name || "-",
              to_account: row.toAccountId?.account_name || "-",
            }))}
            Columns={[
              { id: "date", title: "Date" },
              { id: "from_account", title: "From Account" },
              { id: "to_account", title: "To Account" },
              { id: "amount", title: "Amount" },
              { id: "description", title: "Description" },
            ]}
            setSelected={isSuperAdmin ? setSelectedAdjustment : undefined}
            setOpenDeleteModal={
              isSuperAdmin ? setOpenDeleteModal : undefined
            }
          />
        ))}

      {activeTab === 3 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <MetricCard
            title="Cash"
            value={currentValue.cash.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Cash accounts"
            tone="success"
          />
          <MetricCard
            title="Bank"
            value={currentValue.bank.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Bank accounts"
            tone="info"
          />
          <MetricCard
            title="Receivable"
            value={currentValue.receivable.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Pending from customers"
            tone="warning"
          />
          <MetricCard
            title="Payable"
            value={currentValue.payable.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Pending to companies"
            tone="danger"
          />
          <MetricCard
            title="Total Value"
            value={currentValue.total.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            subtitle="Cash + Bank + Receivable - Payable"
            tone="brand"
          />
        </div>
      )}

      {activeTab === 4 &&
        (CashBookState.loading ? (
          <FetchingLoading />
        ) : (
          <SearchableTable
            SearchPlaceholder="Search transactions..."
            SearchText={searchText}
            setSearchText={setSearchText}
            CurrentData={transactions}
            Columns={[
              { id: "n_date", title: "Date" },
              { id: "source", title: "Type" },
              { id: "party", title: "Party" },
              { id: "desc", title: "Description" },
              { id: "dr", title: "Cash In" },
              { id: "cr", title: "Cash Out" },
              { id: "bal", title: "Balance" },
            ]}
          />
        ))}

      {openAdjustmentModal && (
        <AddCashAdjustmentModal
          open={openAdjustmentModal}
          onClose={() => setOpenAdjustmentModal(false)}
          onSubmit={refreshAdjustments}
        />
      )}

      {openDeleteModal && isSuperAdmin ? (
        <DeleteModal
          Open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Boolean(CashAdjustmentState.deleting)}
          onSubmit={async () => {
            if (!selectedAdjustment?._id) return;
            const result = await dispatch(
              deleteCashAdjustment(selectedAdjustment._id)
            );
            if (deleteCashAdjustment.fulfilled.match(result)) {
              SuccessToast("Deleted — amount returned to source account");
              setOpenDeleteModal(false);
              setSelectedAdjustment(null);
              refreshAdjustments();
              dispatch(fetchAccounts());
            } else {
              ErrorToast(result.payload || "Failed to delete adjustment");
            }
          }}
        />
      ) : null}

    </div>
  );
};

export default Finance;
